import SEO from "../components/seo.jsx"
import { SITE_ORIGIN } from "../siteConfig";
import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import HeroImg from "../assets/female-hair-loss.png"; 

gsap.registerPlugin(ScrollTrigger);

const cinzel = { fontFamily: "'Cinzel', serif" };

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const causes = [
  {
    title: "Hypothyroidism",
    desc: "An underactive thyroid disrupts the normal hair growth cycle, leading to diffuse thinning across the scalp that can develop gradually over time.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <ellipse cx="32" cy="32" rx="14" ry="10" stroke="#c9a84c" strokeWidth="2.5" fill="none"/>
        <circle cx="32" cy="32" r="4" stroke="#c9a84c" strokeWidth="2" fill="none"/>
        <path d="M32 12v6M32 46v6M12 32h6M46 32h6" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Genetic Predisposition",
    desc: "Female pattern hair loss has a strong hereditary component, typically presenting as a diffuse thinning at the crown while the frontal hairline is preserved.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <path d="M24 10c0 8 16 8 16 16s-16 8-16 16" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M40 10c0 8-16 8-16 16s16 8 16 16" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M20 10h24M20 42h24" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round"/>
        <path d="M28 52v4M36 52v4M28 8V4M36 8V4" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Iron Deficiency Anaemia",
    desc: "Low ferritin levels starve hair follicles of essential nutrients, causing increased shedding and reduced density one of the most treatable causes of female hair loss.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <path d="M32 14c-8 0-14 5-14 12 0 10 14 24 14 24s14-14 14-24c0-7-6-12-14-12z" stroke="#c9a84c" strokeWidth="2.5" fill="none"/>
        <path d="M28 26l3 3 6-7" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    title: "Metabolic Disorders",
    desc: "Hormonal imbalances including PCOS, insulin resistance and adrenal dysfunction can trigger or accelerate hair thinning in women of all ages.",
    icon: (
      <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
        <circle cx="32" cy="32" r="18" stroke="#c9a84c" strokeWidth="2.5" fill="none"/>
        <path d="M22 32c0-5.5 4.5-10 10-10s10 4.5 10 10-4.5 10-10 10" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" fill="none"/>
        <circle cx="32" cy="32" r="3" fill="#c9a84c"/>
        <path d="M32 22v4M32 38v4M22 32h4M38 32h4" stroke="#c9a84c" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const treatments = [
  {
    step: "01",
    title: "Thorough Investigation",
    desc: "We begin with a comprehensive evaluation blood panels, trichoscopy, hormonal profiling and complete medical history to pinpoint the exact cause of hair loss.",
  },
  {
    step: "02",
    title: "Elaborate Medical Management",
    desc: "Once the root cause is identified, a personalised medical protocol is initiated targeting thyroid function, iron levels, hormonal balance and scalp health to stop the loss process.",
  },
  {
    step: "03",
    title: "PRP & Regenerative Therapy",
    desc: "Platelet-Rich Plasma injections revive dormant follicles, improve microcirculation at the scalp, and significantly increase hair density without surgery.",
  },
  {
    step: "04",
    title: "Hairline Restoration",
    desc: "For women seeking an attractive and pleasing hairline, we offer highly successful hairline restoration procedures tailored to feminine aesthetics and natural proportion.",
  },
];

const ludwig = [
  { stage: "I", desc: "Mild crown thinning" },
  { stage: "II", desc: "Moderate widening" },
  { stage: "III", desc: "Diffuse thinning" },
];

const faqs = [
  {
    q: "Is hair loss in women different from men?",
    a: "Yes. Female hair loss typically presents as diffuse thinning across the crown rather than a receding hairline. It is often driven by hormonal, nutritional or metabolic factors rather than purely androgenetic causes.",
  },
  {
    q: "How does Clinique investigate female hair loss?",
    a: "We conduct thorough blood work including thyroid function, ferritin, hormonal panels (PCOS screening), and perform trichoscopy to assess the scalp and follicle health before recommending any treatment.",
  },
  {
    q: "Can female hair loss be treated without surgery?",
    a: "In many cases, yes. Medical management targeting the root cause combined with PRP therapy can effectively stop the hair loss process and stimulate regrowth without any surgical procedure.",
  },
  {
    q: "What is hairline restoration for women?",
    a: "It is a surgical procedure that lowers or reshapes the frontal hairline to create a more feminine, attractive and proportionate appearance. It is a very successful procedure with high patient satisfaction.",
  },
  {
    q: "How soon will I see results from treatment?",
    a: "Medical management typically shows improvement within 3–6 months. PRP results are visible from 2–3 sessions. Surgical hairline restoration delivers visible results from 6–9 months post-procedure.",
  },
];

/* ─────────────────────────────────────────────
   FAQ ITEM
───────────────────────────────────────────── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border rounded-xl overflow-hidden transition-colors duration-300 ${open ? "border-[#c9a84c]/40 bg-[#c9a84c]/[0.04]" : "border-[#c9a84c]/15 bg-white"}`}
    >
      <button
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        onClick={() => setOpen((p) => !p)}
      >
        <span style={cinzel} className="text-[13px] font-bold tracking-[0.04em] text-[#1a0f02]">{q}</span>
        <span
          className={`flex-shrink-0 w-7 h-7 rounded-full border border-[#c9a84c]/35 flex items-center justify-center transition-transform duration-300 ${open ? "rotate-45 bg-[#c9a84c] border-[#c9a84c]" : ""}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke={open ? "#1a0f02" : "#c9a84c"} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-[13.5px] leading-[1.8] text-[#6b5535]">{a}</p>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
export default function FemaleHairLoss() {
  const pageRef        = useRef(null);
  const heroEyeRef     = useRef(null);
  const heroHeadRef    = useRef(null);
  const heroSubRef     = useRef(null);
  const introEyeRef    = useRef(null);
  const introHeadRef   = useRef(null);
  const introBody1Ref  = useRef(null);
  const introBody2Ref  = useRef(null);
  const introBody3Ref  = useRef(null);
  const causesRowRef   = useRef(null);
  const causeItemsRef  = useRef([]);
  const ludwigRef      = useRef(null);
  const ludItemsRef    = useRef([]);
  const treatRowRef    = useRef(null);
  const treatItemsRef  = useRef([]);
  const faqRef         = useRef(null);
  const ctaRef         = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const ease = "power3.out";

      const fadeUp = (el, delay = 0) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.75, ease, delay,
            scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      };

      gsap.fromTo(heroEyeRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease });
      gsap.fromTo(heroHeadRef.current, { opacity: 0, y: 26 }, { opacity: 1, y: 0, duration: 0.75, ease, delay: 0.1 });
      gsap.fromTo(heroSubRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.7, ease, delay: 0.18 });

      fadeUp(introEyeRef.current);
      fadeUp(introHeadRef.current, 0.06);
      fadeUp(introBody1Ref.current, 0.1);
      fadeUp(introBody2Ref.current, 0.14);
      fadeUp(introBody3Ref.current, 0.18);

      causeItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, y: 36 },
          { opacity: 1, y: 0, duration: 0.65, ease, delay: i * 0.1,
            scrollTrigger: { trigger: causesRowRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });

      ludItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, scale: 0.9 },
          { opacity: 1, scale: 1, duration: 0.5, ease, delay: i * 0.1,
            scrollTrigger: { trigger: ludwigRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });

      treatItemsRef.current.forEach((el, i) => {
        if (!el) return;
        gsap.fromTo(el,
          { opacity: 0, x: i % 2 === 0 ? -30 : 30 },
          { opacity: 1, x: 0, duration: 0.7, ease, delay: i * 0.08,
            scrollTrigger: { trigger: treatRowRef.current, start: "top 88%", toggleActions: "play none none reverse" } }
        );
      });

      fadeUp(faqRef.current);
      fadeUp(ctaRef.current);

    }, pageRef);
    return () => ctx.revert();
  }, []);

  return (
    <>

    <SEO
      title="Female Hair Loss Causes & Treatment | Clinique Internationale"
      description="Understand female pattern hair loss, the Ludwig scale, and treatment options — from medical management and PRP to hairline restoration at Clinique Internationale."
      url={`${SITE_ORIGIN}/female-hair-loss.php`}
    />


      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&display=swap" rel="stylesheet" />

      <div ref={pageRef} className="relative w-full bg-[#f2ece0] overflow-hidden mt-16 lg:mt-20" style={{ fontFamily: "'Raleway', sans-serif" }}>

  {/* ═══════════════════ HERO BANNER ═══════════════════ */}
  <div className="relative h-[200px] xs:h-[280px] sm:h-[340px] md:h-[600px] flex items-center justify-end overflow-hidden">
    <img
      className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
      src={HeroImg}
      alt="Female hair loss treatment at Clinique Internationale"
    />
    <div className="absolute inset-0" />
    <div
      className="absolute inset-0 opacity-40 pointer-events-none"
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")" }}
    />

    <div className="relative z-10 text-right px-4 sm:px-10 md:px-16 max-w-[85%] sm:max-w-xl">
      <p
        ref={heroEyeRef}
        style={cinzel}
        className="text-[6px] sm:text-[10.5px] tracking-[0.24em] sm:tracking-[0.32em] uppercase text-[#c9a84c] mb-2 sm:mb-4 flex items-center justify-end gap-2 sm:gap-3"
      >
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
        Hair Loss Treatments
        <span className="w-4 sm:w-7 h-px bg-[#c9a84c]" />
      </p>
      <h1
        ref={heroHeadRef}
        style={cinzel}
        className="font-black text-[0.9rem] xs:text-[1.8rem] sm:text-[2.8rem] md:text-[3.6rem] tracking-[0.02em] sm:tracking-[0.04em] uppercase text-white mb-2 sm:mb-3 leading-[1.15] sm:leading-tight"
      >
        Female Hair Loss
      </h1>
      <p ref={heroSubRef} className="text-[11px] sm:text-[15px] text-white/70 leading-relaxed ml-auto">
        Restoring confidence and femininity through expert diagnosis, medical care
        and beautiful hairline restoration.
      </p>
    </div>
  </div>

        {/* ─── breadcrumb ─── */}
        <div className="bg-[#0e0905]/95 border-b border-[#c9a84c]/10">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-3 flex items-center gap-2">
            <a href="/" style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]/60 hover:text-[#c9a84c] transition-colors">Home</a>
            <span className="text-[#c9a84c]/30 text-xs">›</span>
            <span style={cinzel} className="text-[10px] tracking-widest uppercase text-[#c9a84c]">Female Hair Loss</span>
          </div>
        </div>

        {/* ═══════════════════ INTRO ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-20 md:pt-24 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">

            {/* LEFT text */}
            <div>
              <p ref={introEyeRef} style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center gap-2.5">
                <span className="w-6 h-px bg-[#c9a84c]" />
                Overview
              </p>
              <h2
                ref={introHeadRef}
                style={cinzel}
                className="font-black text-[1.55rem] sm:text-[1.85rem] md:text-[2.1rem] tracking-[0.03em] uppercase text-[#1a0f02] leading-tight mb-6"
              >
                More Than Hair<br />A Matter of Confidence
              </h2>
              <p ref={introBody1Ref} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                Although hair loss in men can affect psychological well-being, the effect of hair
                loss in women can be even more{" "}
                <strong className="font-semibold text-[#1a0f02]">emotionally crippling</strong>.
                Long and lustrous hair is a leading sign of feminine allure, and hair loss in women
                can severely affect their self-esteem and quality of life.
              </p>
              <p ref={introBody2Ref} className="text-[14.5px] leading-[1.9] text-[#3a2e1e] mb-4">
                Hypothyroidism, genetic predisposition, iron deficiency anaemia and metabolic
                disorders are among the key causes. At{" "}
                <strong className="font-semibold text-[#1a0f02]">Clinique Internationale</strong>,
                we thoroughly investigate the reason for hair loss and initiate an elaborate medical
                management to stop this process at its root.
              </p>
              <p ref={introBody3Ref} className="text-[14.5px] leading-[1.9] text-[#3a2e1e]">
                Today we also encounter women who wish to have their{" "}
                <strong className="font-semibold text-[#1a0f02]">hairline restoration</strong>{" "}
                done a very successful procedure to achieve an attractive and pleasing hairline
                that restores femininity and confidence.
              </p>
            </div>

            {/* RIGHT stat card + emotional note */}
            <div className="flex flex-col gap-5">
              <div className="relative overflow-hidden bg-[#0e0905] border border-[#c9a84c]/20 rounded-2xl p-7">
                <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/50 to-transparent" />
                <p style={cinzel} className="text-[10px] tracking-[0.22em] uppercase text-[#c9a84c]/60 mb-5">At a Glance</p>
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { val: "40%", sub: "of women experience hair loss by age 40" },
                    { val: "4+", sub: "primary root causes we address" },
                    { val: "10,000+", sub: "successful cases at our clinics" },
                    { val: "95%+", sub: "patient satisfaction rate" },
                  ].map((s) => (
                    <div key={s.val}>
                      <div style={cinzel} className="font-black text-[1.6rem] leading-none mb-1 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">
                        {s.val}
                      </div>
                      <div className="text-[11.5px] text-white/45 leading-snug">{s.sub}</div>
                    </div>
                  ))}
                </div>
                <span className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/20 to-transparent" />
              </div>

              {/* doctor note */}
              <div className="relative overflow-hidden bg-white border border-[#c9a84c]/20 rounded-2xl p-6">
                <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                <div className="flex gap-3 items-start">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a84c]/15 to-[#c9a84c]/5 border border-[#c9a84c]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                      <circle cx="12" cy="8" r="4" stroke="#c9a84c" strokeWidth="1.8" fill="none"/>
                      <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="#c9a84c" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
                    </svg>
                  </div>
                  <div>
                    <p style={cinzel} className="text-[11px] font-bold tracking-[0.06em] text-[#1a0f02] mb-1">Dr. Bhavesh Gupta</p>
                    <p className="text-[12.5px] italic leading-[1.75] text-[#6b5535]">
                      "Female hair loss is often multi-factorial. Our approach is always to identify
                      every contributing cause before designing a treatment plan because lasting
                      results require complete understanding."
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ═══════════════════ CAUSES GRID ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
          <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
            <span className="w-6 h-px bg-[#c9a84c]" />
            Root Causes
          </p>
          <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-[#1a0f02] uppercase tracking-[0.06em] mb-10 sm:mb-12">
            Why Women Experience Hair Loss
          </h3>
          <div ref={causesRowRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6">
            {causes.map((c, i) => (
              <div
                key={i}
                ref={(el) => (causeItemsRef.current[i] = el)}
                className="bg-white border border-[#c9a84c]/18 rounded-2xl p-6 relative overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_16px_36px_rgba(90,60,10,0.1)] transition-all duration-300"
              >
                <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/40 to-transparent" />
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#c9a84c]/10 to-[#c9a84c]/[0.03] border border-[#c9a84c]/20 flex items-center justify-center mb-5">
                  {c.icon}
                </div>
                <div style={cinzel} className="text-[13px] font-bold tracking-[0.06em] uppercase text-[#1a0f02] mb-2.5">
                  {c.title}
                </div>
                <p className="text-[13px] leading-[1.75] text-[#6b5535]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════════════ LUDWIG SCALE ═══════════════════ */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
        <div className="bg-[#faf8f4] py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
            <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
              <span className="w-6 h-px bg-[#c9a84c]" />
              Classification
            </p>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-[#1a0f02] uppercase tracking-[0.06em] mb-3">
              The Ludwig Scale
            </h3>
            <p className="text-[14px] text-center text-[#6b5535] max-w-xl mx-auto mb-10 sm:mb-12">
              We use the Ludwig Classification to assess the severity of female pattern hair loss
              and build the most appropriate treatment strategy.
            </p>

            <div ref={ludwigRef} className="flex flex-col sm:flex-row items-stretch gap-5 sm:gap-6 max-w-2xl mx-auto">
              {ludwig.map((l, i) => (
                <div
                  key={i}
                  ref={(el) => (ludItemsRef.current[i] = el)}
                  className="flex-1 flex flex-col items-center text-center rounded-2xl border p-7 relative overflow-hidden transition-all duration-300 hover:shadow-[0_8px_28px_rgba(201,168,76,0.14)] hover:-translate-y-1"
                  style={{
                    background: `rgba(201,168,76,${0.04 + i * 0.04})`,
                    borderColor: `rgba(201,168,76,${0.18 + i * 0.08})`,
                  }}
                >
                  <span className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
                  <span style={cinzel} className="font-black text-[2.4rem] leading-none text-[#1a0f02] mb-2">
                    {l.stage}
                  </span>
                  <div style={cinzel} className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-[#c9a84c] mb-3">
                    Stage {l.stage}
                  </div>
                  {/* density dots visual */}
                  <div className="grid grid-cols-4 gap-[3px] mb-3">
                    {Array.from({ length: 16 }).map((_, j) => (
                      <div
                        key={j}
                        className="w-2 h-2 rounded-full"
                        style={{ background: j < (16 - i * 5) ? "#c9a84c" : "rgba(201,168,76,0.15)" }}
                      />
                    ))}
                  </div>
                  <p className="text-[12.5px] text-[#6b5535] leading-snug">{l.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-[12px] text-[#a07828] mt-6">
              Stage I (mild) → Stage III (advanced diffuse thinning) · Assessed during your consultation
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />

        {/* ═══════════════════ HAIRLINE RESTORATION HIGHLIGHT ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20">
          <div className="relative overflow-hidden bg-[#0e0905] border border-[#c9a84c]/20 rounded-2xl p-8 sm:p-10 md:p-12">
            <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/50 to-transparent" />
            <span className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/20 to-transparent" />

            {/* decorative large quote mark */}
            <div className="absolute top-4 right-8 sm:right-12" style={{ ...cinzel, fontSize: "140px", lineHeight: 1, color: "rgba(201,168,76,0.05)", userSelect: "none" }}>❦</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center relative z-10">
              <div>
                <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#c9a84c]/70 mb-3 flex items-center gap-2.5">
                  <span className="w-6 h-px bg-[#c9a84c]/50" />
                  Special Procedure
                </p>
                <h3 style={cinzel} className="font-black text-[1.4rem] sm:text-[1.7rem] md:text-[2rem] text-white uppercase tracking-[0.04em] leading-tight mb-4">
                  Hairline Restoration<br />for Women
                </h3>
                <p className="text-[14px] leading-[1.85] text-white/60 mb-6">
                  Increasingly, women come to us wishing to restore or reshape their frontal
                  hairline whether due to hair loss, a naturally high hairline, or post-surgical
                  concerns. This is a highly successful procedure that creates an{" "}
                  <em className="italic" style={{ color: "#c9a84c" }}>attractive and pleasing hairline</em>{" "}
                  tailored to each woman's facial proportions.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Natural Results", "Minimal Downtime", "Permanent", "Feminine Aesthetics"].map((tag) => (
                    <span
                      key={tag}
                      style={cinzel}
                      className="text-[9.5px] tracking-[0.12em] uppercase text-[#c9a84c]/90 border border-[#c9a84c]/25 rounded px-3 py-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { val: "95%+", sub: "Graft survival rate" },
                  { val: "6–9", sub: "Months to see full results" },
                  { val: "1 Day", sub: "Procedure duration" },
                  { val: "Low", sub: "Downtime & recovery" },
                ].map((s) => (
                  <div
                    key={s.val}
                    className="bg-white/[0.04] border border-[#c9a84c]/12 rounded-xl p-5 text-center hover:bg-[#c9a84c]/[0.07] hover:border-[#c9a84c]/25 transition-colors"
                  >
                    <div style={cinzel} className="font-black text-[1.5rem] leading-none mb-1 bg-gradient-to-br from-[#c9943a] via-[#ffe9a0] to-[#c9943a] bg-clip-text text-transparent">
                      {s.val}
                    </div>
                    <div className="text-[11px] text-white/45 leading-snug">{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════ TREATMENT STEPS ═══════════════════ */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
        <div className="bg-[#0e0905] py-16 sm:py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
            <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#c9a84c]/70 mb-3 flex items-center justify-center gap-2.5">
              <span className="w-6 h-px bg-[#c9a84c]/50" />
              Our Approach
            </p>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-white uppercase tracking-[0.06em] mb-10 sm:mb-14">
              Treatment Pathway
            </h3>
            <div ref={treatRowRef} className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {treatments.map((t, i) => (
                <div
                  key={i}
                  ref={(el) => (treatItemsRef.current[i] = el)}
                  className="relative overflow-hidden bg-gradient-to-br from-[#c9a84c]/[0.07] to-[#c9a84c]/[0.02] border border-[#c9a84c]/15 rounded-2xl p-7 hover:border-[#c9a84c]/30 hover:bg-[#c9a84c]/[0.1] transition-all duration-300"
                >
                  <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c9a84c]/35 to-transparent" />
                  <div className="flex items-start gap-5">
                    <span style={cinzel} className="font-black text-[2.2rem] leading-none text-[#c9a84c]/20 select-none flex-shrink-0">
                      {t.step}
                    </span>
                    <div>
                      <div style={cinzel} className="text-[13.5px] font-bold tracking-[0.06em] uppercase text-white/90 mb-2.5">
                        {t.title}
                      </div>
                      <p className="text-[13px] leading-[1.8] text-white/55">{t.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />

        {/* ═══════════════════ FAQs ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20 md:py-24">
          <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
            <span className="w-6 h-px bg-[#c9a84c]" />
            Common Questions
          </p>
          <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.6rem] md:text-[1.8rem] text-center text-[#1a0f02] uppercase tracking-[0.06em] mb-10">
            Frequently Asked Questions
          </h3>
          <div ref={faqRef} className="max-w-3xl mx-auto flex flex-col gap-3">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>

        {/* ═══════════════════ MISSION STRIP ═══════════════════ */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />
        <div className="bg-[#0e0905] py-14 sm:py-16">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <p style={cinzel} className="text-[1rem] sm:text-[1.2rem] md:text-[1.4rem] font-light leading-[1.7] text-white/85 tracking-[0.02em]">
              "We{" "}
              <em className="italic" style={{ color: "#c9a84c" }}>thoroughly investigate</em>{" "}
              the reason for hair loss in every female patient, and initiate an elaborate medical
              management to stop this process because every woman deserves to feel complete."
            </p>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[#c9a84c]/50 mt-5">
             Dr. Bhavesh Gupta, Founder · Clinique Internationale
            </p>
          </div>
        </div>
        <div className="w-full h-px bg-gradient-to-r from-transparent via-[#c9a84c]/30 to-transparent" />

        {/* ═══════════════════ CTA ═══════════════════ */}
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-20 md:py-24">
          <div ref={ctaRef} className="max-w-xl mx-auto text-center">
            <p style={cinzel} className="text-[10.5px] tracking-[0.28em] uppercase text-[#a07828] mb-3 flex items-center justify-center gap-2.5">
              <span className="w-6 h-px bg-[#c9a84c]" />
              Begin Your Journey
            </p>
            <h3 style={cinzel} className="font-black text-[1.3rem] sm:text-[1.65rem] md:text-[1.9rem] text-[#1a0f02] uppercase tracking-[0.06em] mb-3.5">
              Reclaim Your Confidence
            </h3>
            <p className="text-[14px] text-[#6b5535] leading-[1.75] mb-8">
              Whether you're experiencing early thinning or significant loss, our specialists
              will build a treatment plan designed specifically around your needs and goals.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href="/khargharNaviMumbai.php"
                style={{ ...cinzel, backgroundImage: "linear-gradient(135deg,#c9943a,#ffe9a0,#c9943a)", backgroundSize: "200% 100%" }}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-3.5 rounded-md text-[#1a0f02] cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(201,168,76,0.35)] transition-all inline-block"
              >
                Book Consultation
              </a>
              <a
                href="/about-hair-transplants.php"
                style={cinzel}
                className="text-[11px] font-bold tracking-[0.16em] uppercase px-8 py-[13px] rounded-md bg-transparent border border-[#1a0f02]/35 text-[#1a0f02] cursor-pointer hover:-translate-y-0.5 hover:border-[#c9a84c] hover:text-[#a07828] transition-all inline-block"
              >
                View All Treatments
              </a>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}